import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { saveBlob } from "../../lib/files";
import { formatDateTime } from "../../lib/format";

const Section = ({ title, rows, testId, render }) => (
  <details className="rounded-[12px] border border-[#e8edf5] px-3 py-2" data-testid={testId} open={rows.length > 0 && rows.length <= 50}>
    <summary className="cursor-pointer text-sm font-bold text-[#182235]">{title} ({rows.length})</summary>
    <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto text-xs">
      {rows.map((r, i) => <li key={i} className="rounded-lg bg-[#f5f8fd] px-2.5 py-1.5" data-testid={`${testId}-item-${i}`}>{render(r)}</li>)}
    </ul>
  </details>
);

const Client = ({ id, name }) => <span className="font-semibold text-[#182235]">{name} <span className="font-mono text-[#5b667a]">{id}</span></span>;
const Path = ({ r }) => <span className="break-all font-mono text-[#5b667a]">{r.path || r.file}</span>;

export const RunDetailDialog = ({ run, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setData(null); setError("");
    if (run) api.get(`/classify-history/${run.kind}/${run.id}`).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e, "No se pudo cargar el detalle")));
  }, [run]);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/classify-history/${run.kind}/${run.id}/excel`, { responseType: "blob" });
      saveBlob(r.data, `detalle_${run.kind}_${run.id}.xlsx`);
    } catch (e) {
      setError(errorMessage(e, "No se pudo descargar el detalle"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!run} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="run-detail-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-extrabold text-[#182235]">{run?.label}</DialogTitle>
          <DialogDescription className="text-xs text-[#5b667a]">{run && `${formatDateTime(run.at)} · ${run.started_by}`}</DialogDescription>
        </DialogHeader>
        <button type="button" onClick={download} disabled={busy || !data} className="tr-btn-light w-fit px-4 py-2 text-sm" data-testid="run-detail-download">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Descargar detalle (Excel)
        </button>
        {error && <p role="alert" className="text-sm font-semibold text-[#b42318]" data-testid="run-detail-error">{error}</p>}
        {!data && !error && <p className="flex items-center gap-2 text-sm text-[#5b667a]"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}
        {data && (
          <div className="space-y-2.5">
            <Section title="Archivos archivados en un cliente" rows={data.movidos} testId="run-detail-movidos"
              render={(r) => <><Path r={r} /> → <Client id={r.to} name={r.to_name} /></>} />
            <Section title="Ya existían en el expediente" rows={data.ya_existian} testId="run-detail-ya-existian"
              render={(r) => <><Path r={r} /> · ya estaba en <Client id={r.to} name={r.to_name} /></>} />
            <Section title="Enviados a Por clasificar" rows={data.a_bandeja} testId="run-detail-bandeja" render={(r) => <Path r={r} />} />
            <Section title="Clientes eliminados" rows={data.clientes_eliminados} testId="run-detail-eliminados"
              render={(c) => <><Client id={c.borrower_id} name={c.name} /> · {c.files} archivos</>} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
